/**
 * `BusinessDate` — a calendar date with no time and no timezone. ADR-006 §2.
 *
 * A document dated ۱۴۰۵/۰۵/۲۲ is that date in Tehran and in Toronto. That is
 * the whole idea, and it is why this type is a `YYYY-MM-DD` string and **never**
 * a JavaScript `Date` (RD-1).
 *
 * `new Date('2026-08-14')` parses as UTC midnight. Render it in Tehran and you
 * get 03:30 the same day; take a date entered in Tehran, store it as UTC
 * midnight, and render it west of Greenwich and you get the day before. Iran is
 * UTC+03:30, so every off-by-one in this system points the same direction — it
 * would look like a systematic accounting error rather than a formatting bug.
 * This module removes the possibility rather than documenting the hazard.
 *
 * All arithmetic here is integer arithmetic on the proleptic Gregorian
 * calendar. No `Date` is constructed at any point, so there is no zone for a
 * value to be silently interpreted in.
 */
export class InvalidBusinessDate extends Error {
    constructor(value, reason) {
        super(`invalid business date ${JSON.stringify(value)}: ${reason}`);
        this.name = 'InvalidBusinessDate';
    }
}
const SHAPE = /^(-?\d{4,6})-(\d{2})-(\d{2})$/;
/**
 * Validates and brands a `YYYY-MM-DD` string.
 *
 * The shape check alone is not enough — `2026-02-30` and `2026-13-01` both
 * match it. The value is round-tripped through the day count, which rejects
 * every impossible date without a table of month lengths to get wrong.
 */
export function businessDate(value) {
    const match = SHAPE.exec(value);
    if (match === null) {
        throw new InvalidBusinessDate(value, 'expected YYYY-MM-DD');
    }
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (month < 1 || month > 12)
        throw new InvalidBusinessDate(value, `month ${month} is not 1-12`);
    if (day < 1 || day > 31)
        throw new InvalidBusinessDate(value, `day ${day} is not 1-31`);
    const roundTripped = civilFromDays(daysFromCivil(year, month, day));
    if (roundTripped.year !== year || roundTripped.month !== month || roundTripped.day !== day) {
        throw new InvalidBusinessDate(value, `no such date — ${month}/${year} has ${lastDayOfMonth(year, month)} days`);
    }
    return value;
}
export function isBusinessDate(value) {
    if (typeof value !== 'string')
        return false;
    try {
        businessDate(value);
        return true;
    }
    catch {
        return false;
    }
}
/** Builds a date from its parts. Throws rather than rolling over into the next month. */
export function businessDateOf(year, month, day) {
    return businessDate(`${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`);
}
export function partsOf(date) {
    const match = SHAPE.exec(date);
    return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
}
/**
 * Ordering. Zero-padded ISO happens to sort correctly as text, which is exactly
 * why this function exists: somewhere a comparator will be written by hand, and
 * a date with a different width or a negative year would break it silently.
 */
export function compareBusinessDates(a, b) {
    const left = toDayNumber(a);
    const right = toDayNumber(b);
    return left < right ? -1 : left > right ? 1 : 0;
}
export const isBefore = (a, b) => compareBusinessDates(a, b) < 0;
export const isAfter = (a, b) => compareBusinessDates(a, b) > 0;
export const isSameDate = (a, b) => compareBusinessDates(a, b) === 0;
/** Days since 1970-01-01. The canonical integer form for arithmetic. */
export function toDayNumber(date) {
    const { year, month, day } = partsOf(date);
    return daysFromCivil(year, month, day);
}
export function fromDayNumber(days) {
    const { year, month, day } = civilFromDays(days);
    return businessDateOf(year, month, day);
}
export function addDays(date, days) {
    if (!Number.isInteger(days))
        throw new TypeError(`addDays expects an integer, got ${days}`);
    return fromDayNumber(toDayNumber(date) + days);
}
export const subtractDays = (date, days) => addDays(date, -days);
/** `b - a`, in days. Positive when `b` is later. */
export function daysBetween(a, b) {
    return toDayNumber(b) - toDayNumber(a);
}
export function isLeapYear(year) {
    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}
export function lastDayOfMonth(year, month) {
    if (month === 2)
        return isLeapYear(year) ? 29 : 28;
    return month === 4 || month === 6 || month === 9 || month === 11 ? 30 : 31;
}
/**
 * `days_from_civil` and its inverse, from Howard Hinnant's `chrono`-compatible
 * algorithms — the same pair the C++ standard library uses.
 *
 * Written out rather than delegated to `Date` because `Date` is the hazard this
 * module exists to remove. It is transcribed arithmetic on the proleptic
 * Gregorian calendar, which is a closed, fully specified rule; that is a
 * different thing from hand-implementing the Jalali leap rule, which ADR-006
 * JL-4 forbids precisely because it is *not* a simple rule.
 */
function daysFromCivil(year, month, day) {
    const y = year - (month <= 2 ? 1 : 0);
    const era = Math.floor(y / 400);
    const yoe = y - era * 400; // [0, 399]
    const doy = Math.floor((153 * (month + (month > 2 ? -3 : 9)) + 2) / 5) + day - 1; // [0, 365]
    const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy; // [0, 146096]
    return era * 146097 + doe - 719468;
}
function civilFromDays(days) {
    const z = days + 719468;
    const era = Math.floor(z / 146097);
    const doe = z - era * 146097; // [0, 146096]
    const yoe = Math.floor((doe - Math.floor(doe / 1460) + Math.floor(doe / 36524) - Math.floor(doe / 146096)) / 365); // [0, 399]
    const y = yoe + era * 400;
    const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100)); // [0, 365]
    const mp = Math.floor((5 * doy + 2) / 153); // [0, 11]
    const d = doy - Math.floor((153 * mp + 2) / 5) + 1; // [1, 31]
    const m = mp + (mp < 10 ? 3 : -9); // [1, 12]
    return { year: y + (m <= 2 ? 1 : 0), month: m, day: d };
}
//# sourceMappingURL=business-date.js.map